import React from 'react';
import { Card, CardContent, Box } from '@mui/material';
import {
    ResponsiveContainer,
    RadarChart as RechartsRadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Tooltip,
    Legend,
} from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';

interface RadarChartData {
    subject: string;
    value: number;
    fullMark?: number;
    [key: string]: string | number | undefined;
}

interface RadarChartProps extends ChartHeaderProps {
    data: RadarChartData[];
    dataKey?: string;
    color?: string;
    fillOpacity?: number;
    height?: number;
    graphHeight?: number;
    showLegend?: boolean;
    showDots?: boolean;
    strokeWidth?: number;
    dotSize?: number;
    onDetailsClick?: (data?: any) => void;
}

const RadarChart: React.FC<RadarChartProps> = ({
    title,
    description,
    details,
    data,
    dataKey = 'value',
    color = '#8E79FF',
    fillOpacity = 0.6,
    height = 400,
    graphHeight = 400,
    showLegend = true,
    showDots = true,
    strokeWidth = 2,
    dotSize = 4,
    onDetailsClick,
}) => {

    const maxValue = React.useMemo(() => {
        const values = data.map((d) => Number(d[dataKey]) || 0);
        const fullMarks = data.map((d) => Number(d.fullMark) || 0);
        const highest = Math.max(...values, ...fullMarks, 100);
        return highest;
    }, [data, dataKey]);

    const handleRadarClick = (data: any) => {
        if (onDetailsClick) {
            onDetailsClick(data);
        }
        console.log('Clicked Radar Chart Point:');
        console.log('Subject:', data.subject);
        console.log('Value:', data[dataKey]);
        if (data.fullMark) {
            console.log('Full Mark:', data.fullMark);
        }
        console.log('Full Data:', data);
    };

    return (
        <ChartCardWrapper sx={{ height }}>
            <ChartHeader title={title} description={description} details={details} />
            <Box sx={{ position: 'relative' }}>
                <Box
                    sx={{
                        WebkitTapHighlightColor: 'transparent',
                        userSelect: 'none',
                        '& .recharts-polar-grid-angle line': {
                            stroke: '#E5E7EB',
                        },
                        '& .recharts-polar-grid-concentric-circle': {
                            stroke: '#E5E7EB',
                        },
                        '& .recharts-polar-angle-axis-tick-value': {
                            fill: '#6B7280',
                            fontSize: 12,
                        },
                        '& .recharts-polar-radius-axis-tick-value': {
                            fill: '#9CA3AF',
                            fontSize: 10,
                        },
                        '& .recharts-radar-polygon': {
                            transition: 'all 0.3s ease',
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                        },
                        '& .recharts-radar-polygon:hover': {
                            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                        },
                    }}
                >
                    <ResponsiveContainer width="100%" height={graphHeight}>
                        <RechartsRadarChart
                            data={data}
                            margin={{ top: 10, right: 10, bottom: 20, left: 30 }}
                        >
                            <PolarGrid
                                stroke="#E5E7EB"
                                strokeWidth={1}
                            />
                            <PolarAngleAxis
                                dataKey="subject"
                                tick={{ fill: '#6B7280', fontSize: 12 }}
                            />
                            <PolarRadiusAxis
                                angle={90}
                                domain={[0, maxValue]}
                                tick={({ x, y, payload }: any) => {
                                    const isTop = payload && payload.value === maxValue;
                                    const dy = isTop ? 10 : 0;
                                    return (
                                        <text
                                            x={x}
                                            y={y}
                                            fill="#9CA3AF"
                                            fontSize={10}
                                            textAnchor="start"
                                            dy={dy}
                                        >
                                            {payload && payload.value}
                                        </text>
                                    );
                                }}
                            />
                            <Radar
                                name={title}
                                dataKey={dataKey}
                                stroke={color}
                                fill={color}
                                fillOpacity={fillOpacity}
                                strokeWidth={strokeWidth}
                                dot={showDots ? {
                                    r: dotSize,
                                    fill: color,
                                    stroke: '#fff',
                                    strokeWidth: 1,
                                    onClick: (e: any, payload: any) => handleRadarClick(payload.payload),
                                    cursor: 'pointer'
                                } : false}
                                activeDot={
                                    showDots
                                        ? {
                                            r: dotSize + 2,
                                            fill: color,
                                            stroke: '#fff',
                                            strokeWidth: 2,
                                            onClick: (e: any, payload: any) => handleRadarClick(payload.payload),
                                            cursor: 'pointer'
                                        }
                                        : false
                                }
                                style={{ cursor: 'pointer' }}
                            />

                            <Tooltip
                                wrapperStyle={{ zIndex: 9999 }}
                                content={(tooltipProps: any) => {
                                    const { active, payload } = tooltipProps || {};
                                    if (!active || !payload || !payload.length) return null;
                                    const entry = payload[0];
                                    const data = entry.payload as RadarChartData;
                                    const fill = color;
                                    const value = Number(data?.[dataKey]) || 0;
                                    const fullMark = data?.fullMark;

                                    return (
                                        <div
                                            style={{
                                                background: '#fff',
                                                borderRadius: 8,
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                                padding: '8px 12px',
                                                border: '1px solid rgba(0,0,0,0.08)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        background: fill,
                                                        borderRadius: 2
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        color: '#111827',
                                                        fontSize: 13,
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {data?.subject || ''}
                                                </span>
                                                <span style={{ color: '#6b7280', fontSize: 13 }}>:</span>
                                                <span
                                                    style={{
                                                        color: '#111827',
                                                        fontSize: 13,
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {value}
                                                    {fullMark ? ` / ${fullMark}` : ''}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                }}
                            />

                            {showLegend && (
                                <Legend
                                    verticalAlign="bottom"
                                    align="center"
                                    wrapperStyle={{
                                        paddingTop: 20,
                                        fontSize: 12,
                                    }}
                                    iconType="circle"
                                />
                            )}
                        </RechartsRadarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
        </ChartCardWrapper>
    );
};

export default RadarChart;
